// Project → Costs (ERP-PLAN §5.13): what one unit of each variant costs, landed, against its price. A waterfall per
// variant (Materials → +Assembly → +Packaging → +Freight & tariffs → +Tooling = landed cost, against the price, leaving
// the margin), planned or actual (the latest run with output), variance chips, what-if sliders (tariff %, freight, one
// part's price), the planned freight and duty rates, the tooling ledger, and the explicit "Set Shopify cost to landed
// cost". Every number is shared/costs.ts' and explains itself on hover. Its lists are the usual table (DataTable: views,
// columns, Copy / CSV): the variants (a band per product), pre-sales (a run's under it), tooling, duty rates (typed into)
// and the parts without a cost.
import { Plus, RotateCcw, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { revisionState } from "../../../../shared/boms.ts";
import { numberOf } from "../../../../shared/calc.ts";
import {
  type CostIndex,
  projectCosts,
  type Stack,
  shopifyCostRows,
  type ToolingStatus,
  toolingStatus,
  type Variance,
  type WhatIf,
} from "../../../../shared/costs.ts";
import type { DutyRate, ToolingItem } from "../../../../shared/db.gen.ts";
import { type PresaleResult, type PresaleState, presaleResult, runPresales } from "../../../../shared/presales.ts";
import type { GapItem, ProjectIndex, ScopeProduct } from "../../../../shared/projects.ts";
import { runLabel, TOOLING_KIND_LABELS, TOOLING_KINDS, type ToolingKind } from "../../../../shared/runs.ts";
import { bomLink, marginTone, STATE_TONE } from "../../lib/boms.ts";
import { centsText, shortDate, signedCents, usd4, useCostIndex } from "../../lib/costs.ts";
import { money, qty } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { usePresaleIndex } from "../../lib/presales.ts";
import { Link, setSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Attachments } from "../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select, UpperInput } from "../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../ui/DataTable.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { toast } from "../../ui/toast.tsx";
import { Why } from "../../ui/Why.tsx";
import { TableCard } from "./ProjectsPage.tsx";
import { Legend, StackBar, Waterfall } from "./runs/charts.tsx";
import { stackLines } from "./runs/RunPlan.tsx";
import { VarianceChips } from "./runs/RunResult.tsx";
import { Card, StepGaps, type TabProps } from "./Workspace.tsx";

type Mode = "planned" | "actual";
type CostedProduct = ScopeProduct & { bom: NonNullable<ScopeProduct["bom"]> };
type CostRow = { id: string; p: CostedProduct; v: ScopeProduct["variants"][number] };
const pct = (m: number | null | undefined) => (m == null ? "—" : `${Math.round(m * 100)}%`);

export function CostsTab({ project, px, scope, r, here }: TabProps) {
  const cx_ = useCostIndex();
  const variants = useMemo(() => scope.products.flatMap((p) => p.variants.map((v) => v.id)), [scope]);
  const [mode, setMode] = useState<Mode>("planned");
  const [picked, setPicked] = useState<string | null>(null);
  const [whatIf, setWhatIf] = useState<WhatIf>({});
  const [pushing, setPushing] = useState(false);
  const pc = projectCosts(cx_, project.id, variants);
  const trying = whatIf.dutyPct != null || whatIf.freightRate != null || !!whatIf.part;
  const wi = useMemo(() => (trying ? projectCosts(cx_, project.id, variants, whatIf) : null), [cx_, project.id, variants, whatIf, trying]);
  const latest = pc.latest;
  const stackOf = (v: string, m: Mode) => (m === "planned" ? pc.planned.get(v) : latest?.result.actual.get(v));
  // In the latest run's plan, but the run closed without building it: no actual cost.
  const notBuilt = (v: string) => !!latest?.result.units.get(v)?.notBuilt;
  const shown = (v: string) => (trying ? wi?.planned.get(v) : stackOf(v, mode));
  const selected = picked && variants.includes(picked) ? picked : (variants.find((v) => pc.planned.get(v)) ?? null);
  const sel = selected ? shown(selected) : undefined;
  const compare = selected ? (trying ? pc.planned.get(selected) : stackOf(selected, mode === "planned" ? "actual" : "planned")) : undefined;
  const max = Math.max(1, ...variants.flatMap((v) => [shown(v)?.totalCents ?? 0, Math.round((shown(v)?.price ?? 0) * 100)]));
  const missing = r.steps.find((s) => s.key === "costs")?.gaps.find((g) => g.key === "parts_without_cost");
  const noCostText = (v: string) => (!trying && mode === "actual" && notBuilt(v) ? `not built in ${latest?.run.number}` : "no cost yet");
  // Every costed product's variants in one table, a band per product (its BOM and Shopify's cost now); a row picks the
  // variant the waterfall shows (j/k step through them).
  const costProducts = scope.products.filter((p): p is CostedProduct => !!p.bom);
  const costRows: CostRow[] = costProducts.flatMap((p) => p.variants.map((v) => ({ id: v.id, p, v })));
  const costBands = new Map(
    costProducts.map((p, order): [string, TableSection] => [p.link.id, { key: p.link.id, order, label: <CostBand p={p} ix={px.ix} /> }]),
  );
  const vsDelta = (id: string) => {
    const s = shown(id);
    const planned = pc.planned.get(id);
    if (trying) return s && planned ? s.totalCents - planned.totalCents : null;
    return latest?.result.variance.get(id)?.total ?? null;
  };
  const costColumns: Column<CostRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(100px,130px)",
      value: (x) => x.v.sku,
      cell: (x) => <span className="truncate font-mono text-[12px]">{x.v.sku}</span>,
    },
    { key: "variant", header: "Variant", width: "minmax(120px,170px)", value: (x) => x.v.shopify_variant_title || x.v.name },
    {
      key: "stack",
      header: "Per unit",
      headerTitle: "Landed cost per unit, layer by layer, against Shopify's price (the black tick)",
      width: "minmax(240px,1fr)",
      value: (x) => shown(x.v.id)?.totalCents ?? null,
      text: (x) => (shown(x.v.id) ? "" : noCostText(x.v.id)),
      cell: (x) => {
        const s = shown(x.v.id);
        return s ? <StackBar s={s} max={max} price={s.price} label={x.v.sku} /> : <span className="text-[12px] text-faint">{noCostText(x.v.id)}</span>;
      },
    },
    {
      key: "landed",
      header: "Landed",
      width: "90px",
      align: "right",
      value: (x) => shown(x.v.id)?.totalCents ?? null,
      text: (x) => {
        const s = shown(x.v.id);
        return s ? `${centsText(s.totalCents)}${s.complete ? "" : "*"}` : "";
      },
      cell: (x) => {
        const s = shown(x.v.id);
        return (
          s && (
            <span className="font-mono text-[12px] font-medium">
              <Why
                title={`${x.v.sku}: ${trying ? "what if" : mode} landed cost`}
                lines={stackLines(s)}
                note={[...s.missing.slice(0, 3), ...s.flags.slice(0, 2)].join(" · ") || undefined}
                width={440}
              >
                {s.complete ? centsText(s.totalCents) : <span className="text-warn">{centsText(s.totalCents)}*</span>}
              </Why>
            </span>
          )
        );
      },
    },
    {
      key: "price",
      header: "Price",
      width: "80px",
      align: "right",
      value: (x) => (x.v.price == null ? null : Number(x.v.price)),
      cell: (x) => <span className="font-mono text-[12px]">{money(x.v.price)}</span>,
    },
    {
      key: "margin",
      header: "Margin",
      width: "70px",
      align: "right",
      value: (x) => shown(x.v.id)?.margin ?? null,
      text: (x) => pct(shown(x.v.id)?.margin),
      cell: (x) => <span className={cx("font-mono text-[12px]", marginTone(shown(x.v.id)?.margin))}>{pct(shown(x.v.id)?.margin)}</span>,
    },
    {
      key: "vs",
      header: trying ? "What if vs plan" : "Actual vs plan",
      width: "150px",
      value: (x) => vsDelta(x.v.id),
      text: (x) => {
        const d = vsDelta(x.v.id);
        return d == null ? "" : !trying && d === 0 ? "on plan" : signedCents(d);
      },
      cell: (x) => {
        const s = shown(x.v.id);
        const planned = pc.planned.get(x.v.id);
        const va = latest?.result.variance.get(x.v.id);
        return trying && s && planned ? (
          <span className={cx("font-mono text-[11px]", s.totalCents > planned.totalCents ? "text-danger" : "text-ok")}>
            {signedCents(s.totalCents - planned.totalCents)}
          </span>
        ) : va ? (
          <Why
            title={`${x.v.sku}: actual against plan, per unit`}
            note="Hover the chips under the waterfall for each part"
            lines={varianceLines(va)}
            width={400}
          >
            <span className={cx("font-mono text-[11px]", va.total > 0 ? "text-danger" : va.total < 0 ? "text-ok" : "text-faint")}>
              {va.total === 0 ? "on plan" : signedCents(va.total)}
            </span>
          </Why>
        ) : (
          <span className="text-[11px] text-faint">{!latest ? "no run built yet" : notBuilt(x.v.id) ? "not built in that run" : "not in that run"}</span>
        );
      },
    },
    {
      key: "std_cost",
      header: "Shopify cost",
      headerTitle: "Shopify's own cost now: what Sell items' margin used before a landed cost",
      width: "100px",
      align: "right",
      optional: true,
      value: (x) => (x.v.std_cost == null ? null : Number(x.v.std_cost)),
      cell: (x) => <span className="font-mono text-[12px]">{money(x.v.std_cost)}</span>,
    },
  ];
  const missingColumns: Column<GapItem>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(120px,150px)",
      value: (i) => i.sku ?? "",
      cell: (i) => <span className="truncate font-mono text-[12px]">{i.sku}</span>,
    },
    { key: "label", header: "Part", width: "minmax(180px,1fr)", value: (i) => i.label.replace(`${i.sku} · `, "") },
    {
      key: "detail",
      header: "Why",
      width: "minmax(160px,220px)",
      value: (i) => i.detail ?? "",
      cell: (i) => (
        <span className="truncate text-[12px] text-muted" title={i.detail}>
          {i.detail}
        </span>
      ),
    },
  ];

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-3 p-4">
      <StepGaps r={r} steps={["costs"]} project={project} px={px} here={here} />
      <div className="flex items-center gap-3">
        <div className="flex rounded-md border border-line bg-surface p-0.5">
          {(
            [
              ["planned", "Planned"],
              ["actual", latest ? `Actual · ${latest.run.number}` : "Actual (no run built yet)"],
            ] as const
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              disabled={m === "actual" && !latest}
              onClick={() => {
                setMode(m);
                setWhatIf({});
              }}
              className={cx(
                "h-7 rounded px-3 text-[12px] whitespace-nowrap disabled:opacity-50",
                mode === m && !trying ? "bg-selected font-medium" : "text-muted hover:bg-hover hover:text-text",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-muted">
          {trying
            ? "What if: the planned cost with the sliders' values"
            : mode === "planned"
              ? pc.volumeRun
                ? `Standard cost at ${runLabel(pc.volumeRun)}'s quantities (price breaks), today's quotes and rates`
                : "Standard cost at minimum order quantities (no run yet), today's quotes and rates"
              : `What a good unit of ${latest ? runLabel(latest.run) : "the latest run"} cost, from its POs, invoices and output`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Legend />
          {can("costs.update_shopify_costs") && (
            <Button size="sm" icon={<Upload className="size-3.5" />} onClick={() => setPushing(true)}>
              Set Shopify cost to landed cost
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {costRows.length > 0 && (
          <TableCard
            title="Variants"
            note="Landed cost per unit, layer by layer, against Shopify's price (the black tick); click a variant for its waterfall"
            foot="* some parts have no price, so the margin isn't shown (a missing cost is never counted as zero)."
          >
            {(slot) => (
              <DataTable
                fit={20}
                rows={costRows}
                columns={costColumns}
                sectionOf={(x) => costBands.get(x.p.link.id) ?? null}
                activeId={selected}
                onOpen={(x) => setPicked(x.id)}
                exportName="Variant costs"
                viewsSlot={slot}
                pageFilters={false}
                rowNoun={["variant", "variants"]}
              />
            )}
          </TableCard>
        )}
        {!scope.products.some((p) => p.bom) && (
          <div className="rounded-lg border border-dashed border-line-strong bg-surface p-6 text-center text-muted">No BOM to cost yet.</div>
        )}
        <div className="grid grid-cols-[minmax(0,1fr)_420px] items-start gap-3">
          {sel && selected ? (
            <Card
              title={`${cx_.itemById.get(selected)?.sku} · ${sel.label}`}
              note={trying ? "What if" : mode === "planned" ? "Planned" : `Actual, ${latest?.run.number}`}
            >
              <div className="p-3">
                <Waterfall s={sel} compare={compare} />
                <div className="mt-3 text-[11px] text-faint">
                  {compare ? `Dashed line: ${trying ? "planned" : mode === "planned" ? "actual" : "planned"} ${centsText(compare.totalCents)}. ` : ""}Hover a
                  column for what's in it.
                </div>
                {!trying && latest?.result.variance.get(selected) && (
                  <div className="mt-2">
                    <div className="mb-1 text-[12px] font-medium">Actual against plan, per unit</div>
                    <VarianceChips v={latest.result.variance.get(selected) as NonNullable<ReturnType<typeof latest.result.variance.get>>} />
                  </div>
                )}
                <Margin s={sel} />
              </div>
            </Card>
          ) : (
            <Card title="Waterfall">
              <div className="px-4 py-3 text-muted">
                {selected && !trying && mode === "actual" && notBuilt(selected)
                  ? `${cx_.itemById.get(selected)?.sku} wasn't built in ${latest?.run.number} (closed without it): no actual cost.`
                  : "Pick a variant."}
              </div>
            </Card>
          )}
          {selected && pc.planned.get(selected) && (
            <WhatIfCard s={pc.planned.get(selected) as Stack} projectId={project.id} whatIf={whatIf} setWhatIf={setWhatIf} />
          )}
        </div>
        <PresalesSummary projectId={project.id} />
        <ToolingLedger projectId={project.id} />
        <PlannedRates projectId={project.id} customsBasis={project.customs_basis} />
        <DutyRates />
        {missing && missing.items.length > 0 && (
          <TableCard title="Parts without a cost" note="Give each a vendor price or a standard cost">
            {(slot) => (
              <DataTable
                fit
                rows={missing.items}
                columns={missingColumns}
                onOpen={(i) => setSearch({ open: i.id })}
                exportName="Parts without a cost"
                viewsSlot={slot}
                pageFilters={false}
                rowNoun={["part", "parts"]}
              />
            )}
          </TableCard>
        )}
      </div>
      {pushing && <ShopifyPush projectId={project.id} variants={variants} onClose={() => setPushing(false)} />}
    </div>
  );
}

/** A costed product's band: its title, its BOM revision in use, and Shopify's own cost now. */
function CostBand({ p, ix }: { p: CostedProduct; ix: ProjectIndex["ix"] }) {
  const state = revisionState(ix, p.bom);
  return (
    // Keys on its link are its own, not the table's
    <span role="presentation" onKeyDown={(e) => e.stopPropagation()} className="flex items-end gap-3 font-normal tracking-normal whitespace-nowrap normal-case">
      <span className="text-[13px] font-semibold text-text">{p.title}</span>
      <Link to={bomLink(p.bom.id)} className="flex items-center gap-1.5 text-[12px] text-text hover:underline">
        Rev {p.bom.revision} <Badge tone={STATE_TONE[state]}>{state}</Badge>
      </Link>
      <span className="text-[12px] text-muted" title="What Sell items' margin used before a landed cost">
        Shopify's own cost now: {shopifyRange(p.variants.map((v) => v.std_cost))}
      </span>
    </span>
  );
}

type CostRun = NonNullable<ReturnType<CostIndex["runsByProject"]["get"]>>[number];
/** A pre-sale under its run: a run with several has a row of its own above them (its chips); one with one is one row. */
type PresaleRow = { id: string; run: CostRun; s: PresaleState; res: PresaleResult | null; several: boolean; chips: string };

/**
 * Pre-sales --- each run's pre-sales in one line: what's pre-sold and held, the result, per backer box and per unit
 * (shared/presales.ts presaleResult()); the run's Pre-sales tab has the rest. Nothing when the project has none.
 */
function PresalesSummary({ projectId }: { projectId: string }) {
  const cx_ = useCostIndex();
  const pix = usePresaleIndex();
  const runs = (cx_.runsByProject.get(projectId) ?? []).filter((r) => runPresales(pix, r.id).presales.length || runPresales(pix, r.id).holds.length);
  if (!runs.length) return null;
  const rows: PresaleRow[] = runs.flatMap((run) => {
    const rp = runPresales(pix, run.id);
    const chips = rp.chips.map((c) => c.text).join(" · ");
    // The run's chips (pre-sold, held) are the run's: once, on its own row when it has several pre-sales.
    const several = rp.presales.length > 1;
    return rp.presales.map((s) => ({ id: s.presale.id, run, s, res: presaleResult(cx_, pix, s.presale.id), several, chips }));
  });
  const columns: Column<PresaleRow>[] = [
    {
      key: "run",
      header: "Run",
      width: "110px",
      value: (x) => x.run.number,
      cell: (x, { nested }) => <span className="font-mono text-[12px]">{nested ? "" : x.run.number}</span>,
      groupCell: (rs) => <span className="font-mono text-[12px]">{rs[0]?.run.number}</span>,
    },
    {
      key: "presale",
      header: "Pre-sale",
      width: "minmax(140px,1fr)",
      value: (x) => x.s.presale.name,
      cell: (x) => (
        <span className="truncate text-[12px]">
          {x.s.presale.name}
          {x.s.presale.status === "cancelled" && <span className="text-muted"> (cancelled)</span>}
        </span>
      ),
      groupCell: (rs) => <span className="text-[12px] font-normal text-muted">{rs.length} pre-sales</span>,
    },
    {
      key: "sold",
      header: "Pre-sold",
      width: "minmax(160px,1.2fr)",
      value: (x) => (!x.several && x.chips) || `${qty(x.s.reserved)} reserved`,
      cell: (x) => (
        <span className="truncate text-[12px] text-muted" title={(!x.several && x.chips) || undefined}>
          {(!x.several && x.chips) || `${qty(x.s.reserved)} reserved`}
        </span>
      ),
      groupCell: (rs) => (
        <span className="truncate text-[12px] font-normal text-muted" title={rs[0]?.chips}>
          {rs[0]?.chips}
        </span>
      ),
    },
    {
      key: "result",
      header: "Result",
      width: "110px",
      align: "right",
      value: (x) => x.res?.resultCents ?? null,
      text: (x) => (x.res ? centsText(x.res.resultCents) : ""),
      cell: ({ res }) => (
        <span className={cx("font-mono text-[12px]", res && res.resultCents < 0 && "text-danger")}>
          {res ? (
            <Why
              title="Result"
              note={`${res.why.result}: ${centsText(res.income.netCents)} − ${centsText(res.units.cents)} − ${centsText(res.shipping.cents)}`}
            >
              {centsText(res.resultCents)}
            </Why>
          ) : (
            "—"
          )}
        </span>
      ),
    },
    {
      key: "box",
      header: "Per box",
      width: "100px",
      align: "right",
      value: (x) => x.res?.perBoxCents ?? null,
      text: (x) => (x.res?.perBoxCents == null ? "" : centsText(x.res.perBoxCents)),
      cell: ({ res }) => <span className="font-mono text-[12px]">{res?.perBoxCents == null ? "—" : centsText(res.perBoxCents)}</span>,
    },
    {
      key: "unit",
      header: "Per unit",
      width: "90px",
      align: "right",
      value: (x) => x.res?.perUnitCents ?? null,
      text: (x) => (x.res?.perUnitCents == null ? "" : centsText(x.res.perUnitCents)),
      cell: ({ res }) => <span className="font-mono text-[12px]">{res?.perUnitCents == null ? "—" : centsText(res.perUnitCents)}</span>,
    },
  ];
  return (
    <TableCard title="Pre-sales" note="Units sold before they're built (Kickstarter, PledgeBox, pre-orders): their result, never part of a unit's landed cost">
      {(slot) => (
        <DataTable
          fit
          rows={rows}
          columns={columns}
          groupBy={(x) => (x.several ? x.run.id : null)}
          groupNoun={["run", "runs"]}
          rowNoun={["pre-sale", "pre-sales"]}
          onOpen={(x) => setSearch({ tab: "productions", run: x.run.number, rt: "presales" })}
          exportName="Pre-sales"
          viewsSlot={slot}
          pageFilters={false}
        />
      )}
    </TableCard>
  );
}

/** "$29.00–$38.50" (or "none yet") for Shopify's costs. */
function shopifyRange(costs: (number | null)[]) {
  const n = costs.filter((c): c is number => c != null).map(Number);
  if (!n.length) return "none yet";
  const [lo, hi] = [Math.min(...n), Math.max(...n)];
  return lo === hi ? money(lo) : `${money(lo)}–${money(hi)}`;
}

const VARIANCE_PARTS = [
  ["price", "Price"],
  ["usage", "Usage"],
  ["fees", "Fees"],
  ["freight", "Freight & tariffs"],
  ["volume", "Volume"],
  ["other", "Other"],
  ["setAside", "Set aside"],
] as const;
const varianceLines = (v: Variance) => [
  ...VARIANCE_PARTS.filter(([k]) => v[k] !== 0).map(([k, label]) => ({ label, value: signedCents(v[k]), detail: v.why[k] })),
  { label: "Actual − planned", value: signedCents(v.total), strong: true },
];

function Margin({ s }: { s: Stack }) {
  if (s.price == null) return null;
  const m = Math.round(s.price * 100) - s.totalCents;
  return (
    <div className="mt-3 flex items-center gap-3 rounded-md bg-surface-2 px-3 py-2 text-[12px]">
      <span className="text-muted">Price</span>
      <span className="font-mono">{money(s.price)}</span>
      <span className="text-muted">− landed</span>
      <span className="font-mono">{centsText(s.totalCents)}</span>
      <span className="text-muted">=</span>
      <span className={cx("font-mono font-semibold", marginTone(s.margin))}>
        {centsText(m)} ({pct(s.margin)})
      </span>
    </div>
  );
}

// What if --------------------------------------------------------------------------------------------------------

function WhatIfCard({ s, projectId, whatIf, setWhatIf }: { s: Stack; projectId: string; whatIf: WhatIf; setWhatIf: (w: WhatIf) => void }) {
  const cx_ = useCostIndex();
  const rate = (cx_.freightByProject.get(projectId) ?? []).find((r) => !r.vendor_id);
  const basis = rate?.basis ?? "per_unit";
  const parts = s.lines.filter((l) => l.layer !== "assembly" && l.unit != null && !l.service && !l.charge);
  const part = whatIf.part ? parts.find((p) => p.itemId === whatIf.part?.itemId) : undefined;
  const [partId, setPartId] = useState(parts[0]?.itemId ?? "");
  const chosen = parts.find((p) => p.itemId === partId);
  const duty = whatIf.dutyPct ?? s.customs.pct;
  const freight = whatIf.freightRate ?? Number(rate?.rate ?? 0);
  return (
    <Card
      title="What if"
      note="Every variant's planned cost with these values"
      actions={
        (whatIf.dutyPct != null || whatIf.freightRate != null || whatIf.part) && (
          <Button size="sm" variant="ghost" icon={<RotateCcw className="size-3.5" />} onClick={() => setWhatIf({})}>
            Reset
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-3 p-3 text-[12px]">
        <Slider
          label="Tariff on the customs value"
          value={duty}
          min={0}
          max={150}
          step={0.5}
          text={`${duty}%`}
          hint={`Now ${s.customs.pct}% (${s.customs.hts || "no HTS code"} from ${s.customs.country || "—"}), on ${usd4(s.customs.value)} customs value`}
          onChange={(n) => setWhatIf({ ...whatIf, dutyPct: n })}
        />
        <Slider
          label={basis === "per_unit" ? "Freight, $ a unit (project rate)" : "Freight, % of value (project rate)"}
          value={freight}
          min={0}
          max={basis === "per_unit" ? Math.max(10, Number(rate?.rate ?? 0) * 3) : 30}
          step={basis === "per_unit" ? 0.05 : 0.5}
          text={basis === "per_unit" ? money(freight) : `${freight}%`}
          hint={rate ? `Now ${basis === "per_unit" ? money(rate.rate) : `${rate.rate}%`}` : "No project rate yet (Planned rates below)"}
          onChange={(n) => setWhatIf({ ...whatIf, freightRate: n })}
        />
        {parts.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-muted">One part's price</span>
              <div className="ml-auto w-48">
                <Select
                  value={partId}
                  options={parts.map((p) => ({ value: p.itemId, label: `${p.sku} (${money(p.unit)})` }))}
                  onChange={(e) => {
                    setPartId(e.target.value);
                    setWhatIf({ ...whatIf, part: null });
                  }}
                  className="h-7"
                />
              </div>
            </div>
            {chosen && (
              <Slider
                label=""
                value={part?.unit ?? whatIf.part?.unit ?? (chosen.unit as number)}
                min={0}
                max={Math.max(0.01, (chosen.unit as number) * 3)}
                step={Math.max(0.0001, Number(((chosen.unit as number) / 100).toPrecision(2)))}
                text={money(whatIf.part?.itemId === chosen.itemId ? whatIf.part.unit : chosen.unit)}
                hint={`Now ${money(chosen.unit)} · ${chosen.priceText}`}
                onChange={(n) => setWhatIf({ ...whatIf, part: { itemId: chosen.itemId, unit: Number(n.toFixed(4)) } })}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  text,
  hint,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  text: string;
  hint?: string;
  onChange: (n: number) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      {label && (
        <span className="flex items-center gap-2">
          <span className="text-muted">{label}</span>
          <span className="ml-auto font-mono font-medium">{text}</span>
        </span>
      )}
      {!label && <span className="text-right font-mono font-medium">{text}</span>}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--accent)]"
      />
      {hint && <span className="text-[11px] text-faint">{hint}</span>}
    </label>
  );
}

// Tooling ledger --------------------------------------------------------------------------------------------------

type ToolingRow = { id: string; t: ToolingItem; st: ToolingStatus; share: number; run: CostRun | undefined; vendor: string; kept: string };

function ToolingLedger({ projectId }: { projectId: string }) {
  const cx_ = useCostIndex();
  const runs = cx_.runsByProject.get(projectId) ?? [];
  const [editing, setEditing] = useState<ToolingItem | "new" | null>(null);
  const nameOf = (id: string | null) => {
    const v = cx_.vendorById.get(id ?? "");
    return v ? v.short_name || v.name : "";
  };
  const rows: ToolingRow[] = (cx_.toolingByProject.get(projectId) ?? [])
    .filter((t) => !t.retired)
    .map((t) => {
      const st = toolingStatus(cx_, t);
      return {
        id: t.id,
        t,
        st,
        share: st.spread ? Math.min(1, st.built / st.spread) : st.built ? 1 : 0,
        run: cx_.runById.get(t.paid_by_run_id ?? ""),
        vendor: nameOf(t.vendor_id),
        kept: nameOf(t.kept_vendor_id) || t.kept_note,
      };
    });
  const paidOff = (st: ToolingStatus) =>
    st.spread
      ? `${qty(Math.min(st.built, st.spread))} of ${qty(st.spread)} units${st.paidOff ? ": paid off" : ` · ${usd4(st.perUnit)} a unit`}`
      : "all on the run that paid for it";
  const columns: Column<ToolingRow>[] = [
    {
      key: "name",
      header: "Name",
      width: "minmax(180px,1.2fr)",
      value: (x) => x.t.name,
      cell: (x) => <span className="truncate font-medium">{x.t.name}</span>,
    },
    {
      key: "kind",
      header: "Kind",
      width: "120px",
      value: (x) => TOOLING_KIND_LABELS[x.t.kind],
      cell: (x) => <span className="truncate text-[12px] text-muted">{TOOLING_KIND_LABELS[x.t.kind]}</span>,
    },
    { key: "vendor", header: "Vendor", width: "minmax(100px,0.6fr)", cell: (x) => <span className="truncate text-[12px] text-muted">{x.vendor}</span> },
    {
      key: "kept",
      header: "Kept at",
      width: "minmax(100px,0.6fr)",
      cell: (x) => (
        <span className="truncate text-[12px] text-muted" title={x.kept || undefined}>
          {x.kept}
        </span>
      ),
    },
    {
      key: "cost",
      header: "Cost",
      width: "100px",
      align: "right",
      value: (x) => x.st.usd,
      cell: ({ t, st }) => (
        <span className="font-mono text-[12px]">
          <Why
            title={t.name}
            lines={[
              { label: t.currency === "USD" ? "Cost" : `${money(t.amount, t.currency)} at ${t.usd_rate}`, value: money(st.usd) },
              ...(t.invoice_number ? [{ label: `Invoice ${t.invoice_number}`, value: shortDate(t.invoice_date) }] : []),
              ...(t.paid_on ? [{ label: `Paid ${shortDate(t.paid_on)}${t.paid_method ? ` by ${t.paid_method}` : ""}` }] : []),
            ]}
          >
            {money(st.usd)}
          </Why>
        </span>
      ),
    },
    {
      key: "paid_by",
      header: "Paid by",
      width: "100px",
      value: (x) => x.run?.number ?? "",
      text: (x) => x.run?.number ?? "no run",
      cell: (x) => <span className="text-[12px] text-muted">{x.run ? x.run.number : "no run"}</span>,
    },
    {
      key: "paid_off",
      header: "Paid off",
      width: "minmax(220px,1.2fr)",
      value: (x) => x.share,
      text: (x) => paidOff(x.st),
      cell: ({ st, share }) => (
        <>
          <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded bg-surface-2">
            <span className={cx("block h-full rounded", st.paidOff ? "bg-ok" : "bg-[var(--series-5)]")} style={{ width: `${share * 100}%` }} />
          </span>
          <Why
            title="Paid off"
            lines={st.runs.map((r) => ({ label: `${r.number}: ${qty(r.units)} units ${r.built ? "built" : "planned"}`, value: centsText(r.cents) }))}
            note={st.text}
            width={380}
            className="min-w-0 truncate"
          >
            <span className="text-[11px] text-muted">{paidOff(st)}</span>
          </Why>
        </>
      ),
    },
    {
      key: "files",
      header: "Files",
      width: "minmax(110px,160px)",
      value: () => null,
      cell: (x) => (
        // Its files' own button, not the row's
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation" className="min-w-0">
          <Attachments owner={{ type: "tooling_item", id: x.id }} compact />
        </span>
      ),
    },
  ];
  return (
    <TableCard
      title="Tooling ledger"
      note="Molds, fixtures, certifications and samples: paid once, spread over a planned number of units, then no longer in the unit cost"
      actions={
        can("tooling_item.create") && (
          <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setEditing("new")}>
            Add tooling
          </Button>
        )
      }
    >
      {(slot) => (
        <>
          <DataTable
            fit
            rows={rows}
            columns={columns}
            onOpen={(x) => can("tooling_item.update") && setEditing(x.t)}
            exportName="Tooling ledger"
            viewsSlot={slot}
            pageFilters={false}
            rowNoun={["tool", "tools"]}
            empty="Nothing yet. Add the molds, fixtures and certifications, with the units each is spread over."
          />
          {editing && <ToolingDialog projectId={projectId} t={editing === "new" ? null : editing} runs={runs} onClose={() => setEditing(null)} />}
        </>
      )}
    </TableCard>
  );
}

function ToolingDialog({
  projectId,
  t,
  runs,
  onClose,
}: {
  projectId: string;
  t: ToolingItem | null;
  runs: { id: string; number: string; name: string }[];
  onClose: () => void;
}) {
  const vendors = useRows("vendor");
  const [id] = useState(() => t?.id ?? crypto.randomUUID());
  const [saved, setSaved] = useState(!!t);
  const [d, setD] = useState({
    kind: (t?.kind ?? "mold") as ToolingKind,
    name: t?.name ?? "",
    vendor_id: t?.vendor_id ?? null,
    amount: t ? String(t.amount) : "",
    currency: t?.currency ?? "USD",
    usd_rate: t && t.currency !== "USD" ? String(t.usd_rate) : "",
    invoice_number: t?.invoice_number ?? "",
    invoice_date: t?.invoice_date ? String(t.invoice_date).slice(0, 10) : "",
    paid_by_run_id: t?.paid_by_run_id ?? runs.at(-1)?.id ?? null,
    spread_units: t ? String(t.spread_units) : "5000",
    kept_vendor_id: t?.kept_vendor_id ?? null,
    kept_note: t?.kept_note ?? "",
    wireless: !!(t?.applies_when as Record<string, string[]> | null)?.["Connection type"],
  });
  const usd = d.currency.toUpperCase() === "USD";
  const save = async () => {
    if (!d.name.trim()) return toast.error("Name it");
    const fields = {
      kind: d.kind,
      name: d.name.trim(),
      vendor_id: d.vendor_id,
      amount: numberOf(d.amount),
      currency: d.currency.toUpperCase(),
      ...(usd ? {} : { usd_rate: numberOf(d.usd_rate) }),
      invoice_number: d.invoice_number,
      invoice_date: d.invoice_date || null,
      paid_by_run_id: d.paid_by_run_id,
      spread_units: Math.round(numberOf(d.spread_units)),
      kept_vendor_id: d.kept_vendor_id,
      kept_note: d.kept_note,
    };
    if (!usd && !(numberOf(d.usd_rate) > 0)) return toast.error(`Enter the USD rate for ${d.currency}`);
    const ok = saved
      ? await mutate("tooling_item.update", { id, patch: fields })
      : await mutate("tooling_item.create", { id, project_id: projectId, ...fields });
    if (ok && !saved) {
      setSaved(true);
      toast.ok("Added: attach its invoice below");
    } else if (ok) onClose();
  };
  const vendorOptions = [{ value: "", label: "None" }, ...vendors.filter((v) => !v.archived).map((v) => ({ value: v.id, label: v.short_name || v.name }))];
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-[600px] rounded-xl border border-line bg-surface p-4 shadow-pop">
        <div className="text-[15px] font-semibold">{t ? t.name : "Add tooling"}</div>
        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
          <label className="flex flex-col gap-1">
            <span className="text-muted">Kind</span>
            <Select
              value={d.kind}
              options={TOOLING_KINDS.map((k) => ({ value: k, label: TOOLING_KIND_LABELS[k] }))}
              onChange={(e) => setD({ ...d, kind: e.target.value as ToolingKind })}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Name</span>
            <Input value={d.name} placeholder="Shell molds (top and bottom)" onChange={(e) => setD({ ...d, name: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Vendor</span>
            <Select value={d.vendor_id ?? ""} options={vendorOptions} onChange={(e) => setD({ ...d, vendor_id: e.target.value || null })} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Cost</span>
            <span className="flex gap-1.5">
              <Input value={d.amount} inputMode="decimal" onChange={(e) => setD({ ...d, amount: e.target.value })} className="font-mono" />
              <UpperInput value={d.currency} maxLength={3} onText={(currency) => setD({ ...d, currency })} className="w-16 font-mono" aria-label="Currency" />
              {!usd && (
                <Input value={d.usd_rate} placeholder="USD rate" onChange={(e) => setD({ ...d, usd_rate: e.target.value })} className="w-24 font-mono" />
              )}
            </span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Paid by run</span>
            <Select
              value={d.paid_by_run_id ?? ""}
              options={[{ value: "", label: "None" }, ...runs.map((r) => ({ value: r.id, label: runLabel(r) }))]}
              onChange={(e) => setD({ ...d, paid_by_run_id: e.target.value || null })}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Spread over (units)</span>
            <Input value={d.spread_units} inputMode="numeric" onChange={(e) => setD({ ...d, spread_units: e.target.value })} className="font-mono" />
            <span className="text-[11px] text-faint">0 = all on the run that paid for it</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Invoice number</span>
            <Input value={d.invoice_number} onChange={(e) => setD({ ...d, invoice_number: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Invoice date</span>
            <Input type="date" value={d.invoice_date} onChange={(e) => setD({ ...d, invoice_date: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Kept at</span>
            <Select value={d.kept_vendor_id ?? ""} options={vendorOptions} onChange={(e) => setD({ ...d, kept_vendor_id: e.target.value || null })} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-muted">Where exactly (optional)</span>
            <Input value={d.kept_note} placeholder="Dongguan, rack 4" onChange={(e) => setD({ ...d, kept_note: e.target.value })} />
          </label>
        </div>
        <div className="mt-3">
          <div className="mb-1 text-[12px] font-medium">Invoice and photos</div>
          {saved ? (
            <Attachments owner={{ type: "tooling_item", id }} kind="invoice" />
          ) : (
            <div className="text-[12px] text-faint">Save it first, then drop its files here.</div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2">
          {t && can("tooling_item.update") && (
            <Button
              variant="danger"
              onClick={async () =>
                confirm(`Retire ${t.name}? It stops adding to unit cost.`) &&
                (await mutate("tooling_item.update", { id, patch: { retired: true } })) &&
                onClose()
              }
            >
              Retire
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {saved && !t ? "Done" : "Cancel"}
            </Button>
            <Button variant="primary" onClick={save}>
              {saved ? "Save" : "Add"}
            </Button>
          </div>
        </div>
      </div>
    </DialogLayer>
  );
}

// Planned rates ---------------------------------------------------------------------------------------------------

function PlannedRates({ projectId, customsBasis }: { projectId: string; customsBasis: "full" | "cm_invoice" }) {
  const rates = useRows("freight_rate").filter((r) => r.project_id === projectId);
  const vendors = useRows("vendor");
  const def = rates.find((r) => !r.vendor_id);
  const own = rates.filter((r) => r.vendor_id);
  const [vendor, setVendor] = useState("");
  const editable = can("freight_rate.update");
  const setDefault = (patch: { basis?: "per_unit" | "pct_value"; rate?: number }) =>
    def
      ? mutate("freight_rate.update", { id: def.id, patch })
      : mutate("freight_rate.create", { id: crypto.randomUUID(), project_id: projectId, vendor_id: null, basis: "per_unit", rate: 0, ...patch });
  const name = (id: string | null) => {
    const v = vendors.find((x) => x.id === id);
    return v ? v.short_name || v.name : "";
  };
  return (
    <Card title="Planned rates" note="What the planned cost uses for freight and duty (a run fixes them when it moves to Ordering)">
      <div className="flex flex-col gap-3 p-4 text-[12px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-44 font-medium">Freight, project rate</span>
          <div className="w-44">
            <Select
              value={def?.basis ?? "per_unit"}
              disabled={!editable}
              options={[
                { value: "per_unit", label: "$ per finished unit" },
                { value: "pct_value", label: "% of the unit's value" },
              ]}
              onChange={(e) => setDefault({ basis: e.target.value as "per_unit" | "pct_value" })}
              className="h-7"
            />
          </div>
          <RateInput value={def ? Number(def.rate) : null} disabled={!editable} onSave={(n) => setDefault({ rate: n })} />
          <span className="text-faint">For the whole trip: parts in to the CM and finished goods out to HHL.</span>
        </div>
        {own.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-2">
            <span className="w-44 truncate">{name(r.vendor_id)}'s goods</span>
            <div className="w-44">
              <Select
                value={r.basis}
                disabled={!editable}
                options={[
                  { value: "per_unit", label: "$ per finished unit" },
                  { value: "pct_value", label: "% of its goods' value" },
                ]}
                onChange={(e) => mutate("freight_rate.update", { id: r.id, patch: { basis: e.target.value as "per_unit" | "pct_value" } })}
                className="h-7"
              />
            </div>
            <RateInput value={Number(r.rate)} disabled={!editable} onSave={(n) => mutate("freight_rate.update", { id: r.id, patch: { rate: n } })} />
            {editable && (
              <button type="button" className="text-faint hover:text-danger" onClick={() => mutate("freight_rate.remove", { id: r.id })}>
                Remove
              </button>
            )}
          </div>
        ))}
        {editable && (
          <div className="flex items-center gap-2">
            <span className="w-44 text-muted">A vendor's own rate</span>
            <div className="w-44">
              <Select
                value={vendor}
                options={[
                  { value: "", label: "Choose a vendor…" },
                  ...vendors.filter((v) => !v.archived && !own.some((r) => r.vendor_id === v.id)).map((v) => ({ value: v.id, label: v.short_name || v.name })),
                ]}
                onChange={(e) => setVendor(e.target.value)}
                className="h-7"
              />
            </div>
            <Button
              size="sm"
              disabled={!vendor}
              onClick={async () => {
                if (await mutate("freight_rate.create", { id: crypto.randomUUID(), project_id: projectId, vendor_id: vendor, basis: "pct_value", rate: 0 }))
                  setVendor("");
              }}
            >
              Add
            </Button>
            <span className="text-faint">Its goods then use its rate instead of the project's %.</span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
          <span className="w-44 font-medium">Customs value</span>
          <div className="w-80">
            <Select
              value={customsBasis}
              disabled={!can("project.update")}
              options={[
                { value: "full", label: "Materials + assembly (parts HHL supplied included)" },
                { value: "cm_invoice", label: "Only what the CM charges (its parts + fee)" },
              ]}
              onChange={(e) => mutate("project.update", { id: projectId, patch: { customs_basis: e.target.value as "full" | "cm_invoice" } })}
              className="h-7"
            />
          </div>
          <span className="text-faint">US customs adds parts HHL gives the CM (assists) to the price; freight isn't in it.</span>
        </div>
      </div>
    </Card>
  );
}

function RateInput({ value, onSave, disabled }: { value: number | null; onSave: (n: number) => void; disabled?: boolean }) {
  const [text, setText] = useState<string | null>(null);
  return (
    <Input
      value={text ?? (value == null ? "" : String(value))}
      placeholder="0"
      disabled={disabled}
      inputMode="decimal"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text == null) return;
        const n = numberOf(text);
        if (Number.isFinite(n) && n >= 0 && n !== value) onSave(n);
        setText(null);
      }}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className="h-7 w-24 text-right font-mono"
    />
  );
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The duty and tariff rates, for every project: a table to type into (a click on a cell; Enter saves and moves down). */
function DutyRates() {
  const rows = useRows("duty_rate");
  const editable = can("duty_rate.update");
  // By HTS prefix, then layer, then start date (the table keeps the rest of that order within a prefix)
  const sorted = [...rows].sort(
    (a, b) =>
      a.hts_prefix.localeCompare(b.hts_prefix) ||
      a.layer.localeCompare(b.layer) ||
      String(a.effective_from ?? "").localeCompare(String(b.effective_from ?? "")),
  );
  const text = (k: "layer" | "hts_prefix" | "country" | "notes") => ({
    get: (r: DutyRate) => r[k],
    save: (r: DutyRate, value: string) => {
      mutate("duty_rate.update", { id: r.id, patch: { [k]: value } });
    },
  });
  const day = (k: "effective_from" | "effective_to") => ({
    get: (r: DutyRate) => r[k] ?? "",
    save: (r: DutyRate, value: string) => {
      const v = value.trim();
      if (v && !ISO_DAY.test(v)) return toast.error("A date like 2027-01-31");
      if ((v || null) !== r[k]) mutate("duty_rate.update", { id: r.id, patch: { [k]: v || null } });
    },
  });
  const columns: Column<DutyRate>[] = [
    { key: "layer", header: "Layer", width: "minmax(130px,160px)", edit: text("layer") },
    {
      key: "hts_prefix",
      header: "HTS prefix",
      width: "130px",
      cell: (r) => <span className="font-mono text-[12px]">{r.hts_prefix}</span>,
      edit: text("hts_prefix"),
    },
    { key: "country", header: "Country", width: "80px", cell: (r) => <span className="font-mono text-[12px]">{r.country}</span>, edit: text("country") },
    {
      key: "rate_pct",
      header: "Rate %",
      width: "90px",
      align: "right",
      value: (r) => Number(r.rate_pct),
      cell: (r) => <span className="font-mono text-[12px]">{Number(r.rate_pct)}</span>,
      edit: {
        get: (r) => String(Number(r.rate_pct)),
        save: (r, value) => {
          const n = numberOf(value);
          if (Number.isFinite(n) && n >= 0 && n !== Number(r.rate_pct)) mutate("duty_rate.update", { id: r.id, patch: { rate_pct: n } });
        },
      },
    },
    { key: "effective_from", header: "From", width: "110px", value: (r) => r.effective_from ?? "", edit: day("effective_from") },
    { key: "effective_to", header: "Until", width: "110px", value: (r) => r.effective_to ?? "", edit: day("effective_to") },
    { key: "notes", header: "Notes", width: "minmax(160px,1fr)", edit: text("notes") },
    ...(editable
      ? [
          {
            key: "remove",
            header: "",
            width: "70px",
            value: () => null,
            cell: (r: DutyRate) => (
              <button
                type="button"
                className="text-[11px] text-faint hover:text-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm("Remove this rate?")) mutate("duty_rate.remove", { id: r.id });
                }}
              >
                Remove
              </button>
            ),
          } satisfies Column<DutyRate>,
        ]
      : []),
  ];
  return (
    <TableCard
      title="Duty and tariff rates"
      note="By HTS prefix and country of origin, for every project"
      foot="Layers add up (base duty + Section 301 + …); in a layer the longest prefix wins. Tariffs change often: add a new row with its start date rather than editing an old one."
      actions={
        editable && (
          <Button
            size="sm"
            icon={<Plus className="size-3.5" />}
            onClick={() => mutate("duty_rate.create", { id: crypto.randomUUID(), layer: "Base duty", hts_prefix: "", country: "", rate_pct: 0 })}
          >
            Add a rate
          </Button>
        )
      }
    >
      {(slot) => (
        <DataTable
          fit
          rows={sorted}
          columns={columns}
          defaultSort={{ key: "hts_prefix" }}
          editing={editable}
          exportName="Duty rates"
          viewsSlot={slot}
          pageFilters={false}
          rowNoun={["rate", "rates"]}
          empty="No rates yet, so no duty is planned."
        />
      )}
    </TableCard>
  );
}

// Shopify cost push ----------------------------------------------------------------------------------------------

type PushRow = ReturnType<typeof shopifyCostRows>[number] & { id: string };

function ShopifyPush({ projectId, variants, onClose }: { projectId: string; variants: string[]; onClose: () => void }) {
  const cx_ = useCostIndex();
  const rows = useMemo<PushRow[]>(() => shopifyCostRows(cx_, variants).map((r) => ({ ...r, id: r.item_id })), [cx_, variants]);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(rows.filter((r) => !r.same).map((r) => r.item_id)));
  const [busy, setBusy] = useState(false);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const none = variants.filter((v) => !rows.some((r) => r.item_id === v));
  // A cost already the same can't be ticked: there's nothing to set
  const same = new Set(rows.filter((r) => r.same).map((r) => r.id));
  const confirmPush = async () => {
    const costs = rows.filter((r) => picked.has(r.item_id)).map((r) => ({ item_id: r.item_id, std_cost: r.next }));
    if (!costs.length) return;
    setBusy(true);
    const { ok } = await mutateWithResult("costs.update_shopify_costs", { project_id: projectId, costs });
    setBusy(false);
    if (ok) {
      toast.ok(`${costs.length} Shopify cost${costs.length === 1 ? "" : "s"} queued for Shopify (Sell items shows when they've gone)`);
      onClose();
    }
  };
  const columns: Column<PushRow>[] = [
    { key: "sku", header: "SKU", width: "minmax(100px,130px)", cell: (r) => <span className="truncate font-mono text-[12px]">{r.sku}</span> },
    { key: "label", header: "Variant", width: "minmax(120px,1fr)" },
    {
      key: "current",
      header: "Shopify now",
      width: "100px",
      align: "right",
      value: (r) => r.current,
      cell: (r) => <span className="font-mono text-[12px]">{money(r.current) || "—"}</span>,
    },
    { key: "next", header: "Landed", width: "90px", align: "right", cell: (r) => <span className="font-mono text-[12px] font-medium">{money(r.next)}</span> },
    {
      key: "change",
      header: "Change",
      width: "90px",
      align: "right",
      value: (r) => (r.same ? 0 : r.current == null ? null : Math.round((r.next - r.current) * 100)),
      text: (r) => (r.same ? "same" : r.current == null ? "new" : signedCents(Math.round((r.next - r.current) * 100))),
      cell: (r) => (
        <span className={cx("font-mono text-[12px]", r.same ? "text-faint" : "")}>
          {r.same ? "same" : r.current == null ? "new" : signedCents(Math.round((r.next - r.current) * 100))}
        </span>
      ),
    },
    {
      key: "source",
      header: "From",
      width: "minmax(110px,1fr)",
      value: (r) => (r.source === "actual" ? `Actual, ${r.run}` : "Planned"),
      cell: (r) => (
        <span className="truncate text-[11px] text-muted" title={r.text}>
          {r.source === "actual" ? `Actual, ${r.run}` : "Planned"}
        </span>
      ),
    },
  ];
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-[760px] rounded-xl border border-line bg-surface p-4 shadow-pop">
        <div className="flex items-center gap-2">
          <div className="text-[15px] font-semibold">Set Shopify cost to landed cost</div>
          <div ref={setSlot} className="ml-auto flex items-center gap-1" />
        </div>
        <div className="mt-1 text-[12px] text-muted">
          Shopify's unit cost becomes each variant's landed cost: the actual from the latest closed run that built it, else the planned cost. Nothing changes
          until you confirm; it goes through the catalog editor, like an edit on Sell items.
        </div>
        <div className="mt-3">
          <DataTable
            fit
            rows={rows}
            columns={columns}
            selection={{ selected: picked, onChange: setPicked }}
            selectable={(r) => !same.has(r.id)}
            // A row's click ticks it, as its whole line did
            onOpen={(r) => {
              if (same.has(r.id)) return;
              const next = new Set(picked);
              next.has(r.id) ? next.delete(r.id) : next.add(r.id);
              setPicked(next);
            }}
            exportName="Shopify cost push"
            viewsSlot={slot}
            pageFilters={false}
            rowNoun={["variant", "variants"]}
            empty="No variant has a complete landed cost yet."
          />
        </div>
        {none.length > 0 && (
          <div className="mt-2 text-[11px] text-faint">
            {none.length} variants left out: no complete landed cost (some parts have no price), so Shopify's cost stays.
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !picked.size} onClick={confirmPush}>
            Set {picked.size} Shopify cost{picked.size === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}
